import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, TrendingUp, Lightbulb, Rocket, Cpu, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-16">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <Rocket size={64} className="mx-auto mb-6 text-blue-600" />
        <h1 className="text-5xl font-serif font-bold mb-4">
          About The Central Coast Times
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Central Coast's voice for innovation, technology, and free market progress
        </p>
      </div>

      {/* Mission Statement */}
      <Card className="mb-12 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="pt-8">
          <h2 className="text-2xl font-serif font-bold mb-4 text-center">Our Mission</h2>
          <p className="text-lg text-gray-700 leading-relaxed text-center max-w-3xl mx-auto">
            The Central Coast Times champions innovation, entrepreneurship, and technological progress. 
            We believe in free markets, limited government, and the power of individual initiative to drive positive change. 
            From AI startups to renewable energy breakthroughs, from blockchain solutions to educational innovation, 
            we cover the stories that showcase how technology and free enterprise are building a better future for the Central Coast and beyond.
          </p>
        </CardContent>
      </Card>

      {/* What We Cover */}
      <div className="mb-12">
        <h2 className="text-3xl font-serif font-bold mb-8 text-center">What We Cover</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <Cpu className="text-blue-600" size={28} />
                Technology & Innovation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">
                AI, blockchain, fintech, renewable energy, space tech, biotech, and cutting-edge innovations 
                transforming how we live and work on the Central Coast.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <TrendingUp className="text-green-600" size={28} />
                Business & Entrepreneurship
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">
                Startup success stories, local businesses thriving through innovation, market-driven solutions, 
                and the entrepreneurs building the Central Coast's economic future.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <DollarSign className="text-purple-600" size={28} />
                Free Market Economics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">
                Conservative economic policy, deregulation success stories, private sector solutions, 
                and how free markets create prosperity without government intervention.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <Lightbulb className="text-orange-600" size={28} />
                Personal Responsibility & Success
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">
                Stories of individual achievement, self-improvement, alternative education paths, 
                and people taking control of their futures without relying on government programs.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Why We Exist */}
      <div className="mb-12">
        <h2 className="text-3xl font-serif font-bold mb-6">Why We Exist</h2>
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-700 leading-relaxed mb-4">
            Mainstream media too often focuses on problems, regulations, and government solutions. They ignore the incredible 
            innovations happening in private enterprise and the entrepreneurs building the future right here on the Central Coast.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            <strong>We believe in a different narrative:</strong> one where technology solves problems, where free markets create 
            prosperity, where individuals take responsibility for their success, and where innovation thrives without 
            bureaucratic red tape holding it back.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            From local tech startups automating business operations to renewable energy companies succeeding without taxpayer subsidies, 
            from young people choosing practical tech skills over expensive degrees to blockchain solutions disrupting traditional industries - 
            these are the stories that matter.
          </p>
          <p className="text-gray-700 leading-relaxed">
            <strong>The Central Coast Times is here to celebrate progress, champion innovation, and give voice to the entrepreneurs 
            and innovators building tomorrow.</strong>
          </p>
        </div>
      </div>

      {/* Our Values */}
      <div className="mb-12">
        <h2 className="text-3xl font-serif font-bold mb-6">Our Core Values</h2>
        <div className="space-y-4">
          <div className="border-l-4 border-blue-600 pl-4">
            <h3 className="font-bold text-lg mb-1">Innovation & Progress</h3>
            <p className="text-gray-700">We celebrate technological advancement and the entrepreneurs driving it forward. The future is built by innovators, not regulators.</p>
          </div>
          <div className="border-l-4 border-green-600 pl-4">
            <h3 className="font-bold text-lg mb-1">Free Markets & Limited Government</h3>
            <p className="text-gray-700">Economic freedom creates prosperity. The best solutions come from competition and individual initiative, not government programs.</p>
          </div>
          <div className="border-l-4 border-purple-600 pl-4">
            <h3 className="font-bold text-lg mb-1">Individual Responsibility</h3>
            <p className="text-gray-700">Success comes from personal effort, not government handouts. We highlight people who take control of their destinies.</p>
          </div>
          <div className="border-l-4 border-orange-600 pl-4">
            <h3 className="font-bold text-lg mb-1">Entrepreneurial Spirit</h3>
            <p className="text-gray-700">Small businesses and startups are the backbone of our economy. We support those who take risks and create value.</p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center bg-gradient-to-r from-blue-600 to-indigo-800 text-white rounded-lg p-12">
        <Zap size={48} className="mx-auto mb-4" />
        <h2 className="text-3xl font-serif font-bold mb-4">Share Your Innovation Story</h2>
        <p className="text-blue-100 mb-8 text-lg max-w-2xl mx-auto">
          Have a tech startup, business breakthrough, or innovation story from the Central Coast? 
          We want to showcase the entrepreneurs and innovators building our future.
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <Link to="/submit-story">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-bold">
              Submit Your Story
            </Button>
          </Link>
          <Link to="/share-good-news">
            <Button size="lg" className="bg-green-600 hover:bg-green-700 font-bold">
              Share Success
            </Button>
          </Link>
        </div>
        <p className="text-blue-200 mt-6 text-sm">
          For partnership opportunities, advertising, or media inquiries, contact us today.
        </p>
      </div>
    </div>
  );
};

export default AboutPage;
